import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  updateInspectionData,
  deleteInspection,
  subscribeToDDLByDropdownId,
  subscribeToDDLFullItemsByDropdownId,
  subscribeToDDLFullItemsByName,
} from '../utils/storage';
import { get } from '../api';
import { STATUS_ORDER } from '../utils/inspectionStatus';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import { validateInspectionUrl } from '../utils/urlValidation';
import './InspectionWizardPage.css';

// ── Constants ─────────────────────────────────────────────────────

const STEPS_BASE = ['Info', 'Procedure', 'Examination', 'Equipment', 'NDT Operators'];
const STEPS_UT   = ['Info', 'Procedure', 'Examination', 'Equipment', 'Probe Modules & Stepwedges', 'NDT Operators'];

const MAX_EQUIPMENT     = 5;
const MAX_PROBES        = 5;
const MAX_STEPWEDGES    = 2;
const MAX_NDT_OPERATORS = 4;

const DEF_COUPLANT = 'Water based';

// ── Helpers ───────────────────────────────────────────────────────

// Auto-suggest a Probe Module name from a UTC Module name.
//   "G3UT-021 (Martin)" → "G3UT-021-WRML (Martin)"
//   "G3UT-021"          → "G3UT-021-WRML"
// The wizard then looks this up (case-insensitive) in the Probe Modules
// cache; if it matches a real entry, the probe is auto-added to page 5.
function deriveProbeNameFromModule(moduleName) {
  if (!moduleName) return null;
  const s = String(moduleName).trim();
  if (!s) return null;
  const m = s.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  if (m) {
    const base  = (m[1] || '').trim();
    const paren = m[2] || '';
    return base ? `${base}-WRML (${paren})` : null;
  }
  return `${s}-WRML`;
}

function getCertification(items, operatorLabel) {
  if (!operatorLabel) return null;
  const found = items.find(
    (it) => it.label.trim().toLowerCase() === operatorLabel.trim().toLowerCase()
  );
  if (!found) return null;
  const children = found.children || found.items || [];
  if (children.length === 0) return null;
  return children[0].label || null;
}

/** Returns true if inspection.formData has at least one non-empty field. */
function isInspectionFilled(inspection) {
  const fd = inspection.formData;
  if (!fd) return false;
  return Object.values(fd).some((v) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
}

// ── LockedField ───────────────────────────────────────────────────

function LockedField({ value, defaultValue, onChange, numericOnly = false, allowedRegex = null, placeholder = '' }) {
  const readOnly                = useReadOnly();
  const [locked, setLocked]     = useState(true);
  const [localVal, setLocalVal] = useState(value || defaultValue || '');
  const inputRef                = useRef(null);

  useEffect(() => {
    if (locked) setLocalVal(value || defaultValue || '');
  }, [value, defaultValue, locked]);

  function handleDoubleClick() {
    setLocked(false);
    setLocalVal('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleBlur() {
    if (!localVal.trim()) {
      const restored = defaultValue || '';
      setLocalVal(restored);
      onChange(restored);
    } else {
      onChange(localVal);
    }
    setLocked(true);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.target.blur(); return; }
    if (e.key === 'Escape') { setLocalVal(value || defaultValue || ''); setLocked(true); return; }
    const allowedControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (numericOnly) {
      if (!allowedControl.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
    } else if (allowedRegex) {
      // Single-character key check — control keys (Tab/Backspace etc.)
      // are multi-char names so they pass through naturally.
      if (e.key.length === 1 && !allowedRegex.test(e.key)) e.preventDefault();
    }
  }

  // Archived (read-only): a plain greyed textbox — no padlock, no double-click
  // to "unlock" (which looked editable even though it wasn't).
  if (readOnly) {
    return (
      <div className="locked-field insp-wizard-locked insp-wizard-locked-readonly">
        <span className="locked-field-value">{value || defaultValue || placeholder}</span>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="locked-field insp-wizard-locked" onDoubleClick={handleDoubleClick} title="Double-click to edit">
        <span className="locked-field-value">{value || defaultValue || placeholder}</span>
        <span className="locked-field-icon">🔒</span>
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      className="insp-wizard-locked-input"
      type="text"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder || defaultValue}
    />
  );
}

// ── Expiry-badge helpers (mirrored from EquipmentPage) ────────────

function _expiryClass(iso) {
  if (!iso) return 'ddl-expiry-none';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'ddl-expiry-none';
  const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
  if (diff < 0)    return 'ddl-expiry-expired';
  if (diff <= 30)  return 'ddl-expiry-soon';
  return 'ddl-expiry-set';
}
function _expiryIcon(cls) {
  if (cls === 'ddl-expiry-expired') return '⚠ ';
  if (cls === 'ddl-expiry-soon')    return '⏰ ';
  if (cls === 'ddl-expiry-set')     return '📅 ';
  return '';
}
function _formatExpiryDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── AddEquipmentModal ─────────────────────────────────────────────
//
// Two modes:
//   • If `category` is set (VT/UT/MT inspections), shows a multi-select list
//     of equipment in that category fetched from the backend, with name +
//     expiry badge. User picks any number up to the remaining slot count
//     (MAX_EQUIPMENT − alreadyAdded.length) and adds them in one go.
//   • If `category` is null (DEFO), falls back to a free-text input.

function AddEquipmentModal({ category, alreadyAdded, maxItems, onClose, onAdd, title = 'Add Equipment', emptyLabel }) {
  // ── Multi-select picker (VT/UT/MT) ──────────────────────────────
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await get(`/equipment/by-category/${encodeURIComponent(category)}`);
        if (cancelled) return;
        const list = Array.isArray(resp?.items) ? resp.items : [];
        list.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
        setItems(list);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Failed to load equipment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category]);

  const addedNames = new Set(alreadyAdded.map((n) => String(n).trim().toLowerCase()));
  const remaining  = Math.max(0, maxItems - alreadyAdded.length);

  function toggle(name) {
    const key = name.trim().toLowerCase();
    if (addedNames.has(key)) return; // already in inspection list
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        if (next.size >= remaining) return prev; // cap
        next.add(name);
      }
      return next;
    });
  }

  function handleConfirm() {
    if (selected.size === 0) return;
    onAdd(Array.from(selected));
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="confirm-text">
          Select equipment used in this inspection.
          {' '}<strong>{selected.size}</strong> selected
          {remaining > 0 && <> · up to <strong>{remaining}</strong> more</>}.
        </p>

        {loading && <div className="site-select-empty">Loading equipment…</div>}
        {!loading && error && <div className="equipment-error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="site-select-empty">
            <div className="site-select-empty-icon">🛠️</div>
            <p>No equipment found in {category}.</p>
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="site-select-list">
            {items.map((it) => {
              const name        = it.name || '(unnamed)';
              const isAdded     = addedNames.has(name.trim().toLowerCase());
              const isSelected  = selected.has(name);
              const expIso      = it.dateEndCertificateValidity || null;
              const expCls      = _expiryClass(expIso);
              const expDate     = _formatExpiryDate(expIso);
              const atCap       = !isSelected && !isAdded && selected.size >= remaining;
              const rowCls = [
                'site-select-item',
                isAdded ? 'insp-wizard-op-added' : '',
                isSelected ? 'insp-wizard-op-selected' : '',
                atCap ? 'insp-wizard-op-disabled' : '',
              ].filter(Boolean).join(' ');
              return (
                <div
                  key={it.id || name}
                  className={rowCls}
                  onClick={() => !isAdded && !atCap && toggle(name)}
                >
                  <div style={{ flex: 1 }}>
                    <div className="site-select-item-name">{name}</div>
                    {it.manufacturer && (
                      <div className="site-select-item-meta">{it.manufacturer}</div>
                    )}
                  </div>
                  {expIso && (
                    <span className={`ddl-expiry-badge ${expCls}`}>
                      {_expiryIcon(expCls)}{expDate}
                    </span>
                  )}
                  {isAdded
                    ? <span className="insp-wizard-op-added-icon" title="Already added">✓</span>
                    : isSelected
                      ? <span className="insp-wizard-op-selected-icon" title="Selected">●</span>
                      : <span className="site-select-item-arrow">›</span>
                  }
                </div>
              );
            })}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={selected.size === 0}
          >
            Add {selected.size > 0 ? `${selected.size} ` : ''}Selected
          </button>
        </div>
      </div>
    </div>
  );
}

// Legacy free-text equipment input (DEFO inspection only).
function FreeTextEquipmentModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAdd([trimmed]);
    onClose();
  }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Equipment</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="confirm-text">Enter the equipment name or description.</p>
        <div className="form-group">
          <label>Equipment</label>
          <input
            className="assign-select-full"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="e.g. GE Everest XL Pro"
            autoFocus
          />
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={!name.trim()}>Add</button>
        </div>
      </div>
    </div>
  );
}

// ── AddNDTOperatorModal ───────────────────────────────────────────
//
// Multi-select: user toggles operators on/off in one modal session and
// adds them all in one click. Cap = MAX_NDT_OPERATORS − alreadyAdded.length.

function AddNDTOperatorModal({ ndtOperatorCollection, alreadyAdded, maxItems, onClose, onAdd }) {
  const [operatorItems, setOperatorItems] = useState([]);
  const [selected, setSelected]           = useState(() => new Set());

  useEffect(() => subscribeToDDLFullItemsByName(ndtOperatorCollection, setOperatorItems), [ndtOperatorCollection]);

  const addedNames = new Set(alreadyAdded.map((o) => o.name.trim().toLowerCase()));
  const remaining  = Math.max(0, maxItems - alreadyAdded.length);

  function toggle(label) {
    const key = label.trim().toLowerCase();
    if (addedNames.has(key)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        if (next.size >= remaining) return prev;
        next.add(label);
      }
      return next;
    });
  }

  function handleConfirm() {
    if (selected.size === 0) return;
    const ops = Array.from(selected).map((label) => ({
      name: label,
      certification: getCertification(operatorItems, label) || '',
    }));
    onAdd(ops);
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add NDT Operator</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="confirm-text">
          Select qualified NDT operators.
          {' '}<strong>{selected.size}</strong> selected
          {remaining > 0 && <> · up to <strong>{remaining}</strong> more</>}.
        </p>
        {operatorItems.length === 0 ? (
          <div className="site-select-empty">
            <div className="site-select-empty-icon">👷</div>
            <p>No NDT operators configured yet.</p>
          </div>
        ) : (
          <div className="site-select-list">
            {operatorItems.map((item, idx) => {
              const isAdded    = addedNames.has(item.label.trim().toLowerCase());
              const isSelected = selected.has(item.label);
              const cert       = getCertification(operatorItems, item.label);
              const atCap      = !isSelected && !isAdded && selected.size >= remaining;
              const rowCls = [
                'site-select-item',
                isAdded ? 'insp-wizard-op-added' : '',
                isSelected ? 'insp-wizard-op-selected' : '',
                atCap ? 'insp-wizard-op-disabled' : '',
              ].filter(Boolean).join(' ');
              return (
                <div
                  key={idx}
                  className={rowCls}
                  onClick={() => !isAdded && !atCap && toggle(item.label)}
                >
                  <div style={{ flex: 1 }}>
                    <div className="site-select-item-name">{item.label}</div>
                    {cert && <div className="site-select-item-meta">{cert}</div>}
                  </div>
                  {isAdded
                    ? <span className="insp-wizard-op-added-icon" title="Already added">✓</span>
                    : isSelected
                      ? <span className="insp-wizard-op-selected-icon" title="Selected">●</span>
                      : <span className="site-select-item-arrow">›</span>
                  }
                </div>
              );
            })}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={selected.size === 0}
          >
            Add {selected.size > 0 ? `${selected.size} ` : ''}Selected
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main: InspectionWizardPage ─────────────────────────────────────

export default function InspectionWizardPage({
  inspection, inspectionId, clientId, objectId, onClose,
  typeLabel, objectTag,
  typePrefix,
  ndtOperatorCollection,
  equipmentCategory = null,
  defaults = {},
  objectStartDate = null,
  effectiveStatus = '',
}) {
  const navigate = useNavigate();
  const saved    = inspection.formData || {};
  const filled   = isInspectionFilled(inspection);
  const readOnly = useReadOnly();   // archived → display-only "Show more"

  // ── Derived defaults ─────────────────────────────────────────
  const DEF_SUBJECT              = defaults.subject            ?? '';
  const DEF_SPECIFIC_CODE        = defaults.specificCode       ?? '';
  const DEF_ACCEPTANCE_STANDARD  = defaults.acceptanceStandard ?? 'Not applicable';
  const DEF_TECHNIQUE            = defaults.technique          ?? '';
  const DEF_EXTENT_OF_TESTING    = defaults.extentOfTesting    ?? 'As per inspection plan';
  const DEF_SURFACE_TEMP         = defaults.surfaceTemp        ?? 'Ambient';

  // ── Step + UI state ───────────────────────────────────────────
  const [step,        setStep]        = useState(0);
  const [deleteStage, setDeleteStage] = useState(0); // 0 = idle, 1 = awaiting confirm
  const [saving,      setSaving]      = useState(false);

  // Page 0 — Info
  // The dropdown edits the MANUAL status (the user's intent). The computed
  // effectiveStatus (from the backend) is shown read-only beside it, so an
  // auto value like "Delayed" is never silently saved over the manual choice.
  const [inspectionStatus, setInspectionStatus] = useState(inspection.status || 'Planning');

  // Re-sync only when the saved status itself changes (or a different
  // inspection loads) — NOT on every background poll, which hands us a fresh
  // `inspection` object each 15 s and would wipe an unsaved dropdown choice.
  useEffect(() => {
    setInspectionStatus(inspection.status || 'Planning');
  }, [inspection.id, inspection.status]);

  // Before start date → dropdown is locked to "Planning" and cannot be changed.
  // Prefer the inspection's own startDate; fall back to the object's campaign startDate.
  const today          = new Date();
  today.setHours(0, 0, 0, 0);
  const startStr       = inspection.startDate || objectStartDate || null;
  const inspStartDate  = startStr ? new Date(startStr + 'T00:00:00') : null;
  const isBeforeStart  = inspStartDate !== null && today < inspStartDate;
  const [threeDInspectUrl, setThreeDInspectUrl] = useState(saved.threeDInspectUrl ?? '');
  // Validation hint shown beneath the 3D Inspect URL field on blur.
  // Cleared on focus so the user has a clean slate when retyping.
  const [inspUrlHint, setInspUrlHint] = useState('');
  const inspUrlInputRef = useRef(null);

  // Page 1 — Procedure
  const [specificCode,       setSpecificCode]       = useState(saved.specificCode       ?? DEF_SPECIFIC_CODE);
  const [terraProcedure,     setTerraProcedure]     = useState(saved.terraProcedure     ?? '');
  const [acceptanceStandard, setAcceptanceStandard] = useState(saved.acceptanceStandard ?? DEF_ACCEPTANCE_STANDARD);

  // UT inspections get extra fields: couplant + probes/stepwedges page.
  // The 'ut' typePrefix covers the umbrella Ultrasonic Inspection type
  // (mirrors UT-OSI / UT-ISI exactly per user spec).
  const isUT  = typePrefix === 'utosi' || typePrefix === 'utisi' || typePrefix === 'ut';
  const STEPS = isUT ? STEPS_UT : STEPS_BASE;

  // Page 2 — Examination
  const [subject,          setSubject]          = useState(saved.subject          ?? DEF_SUBJECT);
  const [technique,        setTechnique]        = useState(saved.technique        ?? DEF_TECHNIQUE);
  const [typeOfInspection, setTypeOfInspection] = useState(saved.typeOfInspection ?? '');
  const [extentOfTesting,  setExtentOfTesting]  = useState(saved.extentOfTesting  ?? DEF_EXTENT_OF_TESTING);
  const [surfaceCondition, setSurfaceCondition] = useState(saved.surfaceCondition ?? '');
  const [surfaceTemp,      setSurfaceTemp]      = useState(saved.surfaceTemp      ?? DEF_SURFACE_TEMP);
  const [insideOutside,    setInsideOutside]    = useState(saved.insideOutside    ?? '');
  const [manualOrDrone,    setManualOrDrone]    = useState(saved.manualOrDrone    ?? '');
  const [couplant,         setCouplant]         = useState(saved.couplant         ?? DEF_COUPLANT);
  // Examination-page extras for MT + PT. The two inspections share
  // typeOfWelding + methodOfWelding (same dropdowns); the other two
  // fields differ between MT and PT and are gated in renderPage2.
  //   MT only: typeOfMagnetization, materialThickness ("10-16" default)
  //   PT only: applicationMethod, processingTime
  const DEF_MATERIAL_THICKNESS = '10-16';
  const [typeOfMagnetization, setTypeOfMagnetization] = useState(saved.typeOfMagnetization ?? '');
  const [materialThickness,   setMaterialThickness]   = useState(saved.materialThickness   ?? DEF_MATERIAL_THICKNESS);
  const [applicationMethod,   setApplicationMethod]   = useState(saved.applicationMethod   ?? '');
  const [processingTime,      setProcessingTime]      = useState(saved.processingTime      ?? '');
  const [typeOfWelding,       setTypeOfWelding]       = useState(saved.typeOfWelding       ?? '');
  const [methodOfWelding,     setMethodOfWelding]     = useState(saved.methodOfWelding     ?? '');

  // Page 3 — Equipment
  // CW-OSI is locked to a single "Digital weld gauge" entry — the
  // inspection is simple enough that the equipment list is fixed.
  // The wizard hides the add/remove buttons (see renderPage3) and the
  // saved value is always forced to the canonical single-element list,
  // overriding any previously-saved state.
  const isCW = typePrefix === 'cwosi';
  // MT (all three flavours: MT-OSI / MT-ISI / MT umbrella) is locked
  // to a fixed five-piece equipment list. Same hide-add/remove UX as
  // CW-OSI — see renderPage3.
  const isMT = typePrefix === 'mtosi' || typePrefix === 'mtisi' || typePrefix === 'mt';
  // PT (Penetrant Testing) — equipment selected through a 4-category
  // picker. One option per category, picker can be left blank
  // (surfaces as a warning, not an error).
  const isPT = typePrefix === 'ptosi' || typePrefix === 'ptisi' || typePrefix === 'pt';
  // DEFO — single "3D Scanner" dropdown, same category-picker pattern as PT/MT.
  const isDEFO = typePrefix === 'defo';
  // MT equipment — same per-category picker pattern as PT. Four
  // single-default categories + one Lux Meter category with two
  // options. Selections drive a `mtEquipmentPicks` map and the
  // derived `equipment` array.
  const MT_EQUIPMENT_CATEGORIES = [
    { key: 'magneticYoke',   label: 'Magnetic Yoke',   options: ['Matcon MAT 42 TS'],                    defaultValue: 'Matcon MAT 42 TS' },
    { key: 'penetrameter',   label: 'Penetrameter',    options: ['Berthold Magnetic Field Indicator'],   defaultValue: 'Berthold Magnetic Field Indicator' },
    { key: 'contrastPaint',  label: 'Contrast Paint',  options: ['MET-L-Chek® Matpaint'],                defaultValue: 'MET-L-Chek® Matpaint' },
    { key: 'testSuspension', label: 'Test Suspension', options: ['MET-L-Chek® Matblack'],                defaultValue: 'MET-L-Chek® Matblack' },
    { key: 'luxMeter',       label: 'Lux Meter',       options: ['Voltcraft LX-10', 'PCE-172'],          defaultValue: '' },
  ];
  // Each PT_EQUIPMENT_CATEGORIES entry yields one <select> on page 3.
  // Order here = render order on the page; also drives the order the
  // selected names land in the final `equipment` array.
  const PT_EQUIPMENT_CATEGORIES = [
    { key: 'penetrantLiquid',  label: 'Penetrant Liquid',  options: ['Magnaflux SKL-WP2', 'MET-L-Chek® VP30'] },
    { key: 'penetrantRemover', label: 'Penetrant Remover', options: ['Water', 'Magnaflux SKC-S', 'MET-L-Chek® E-59A'] },
    { key: 'developer',        label: 'Developer',         options: ['Magnaflux SKD-S2', 'MET-L-Chek® D-70'] },
    { key: 'luxMeter',         label: 'Lux Meter',         options: ['Voltcraft LX-10', 'PCE-172'] },
  ];
  // DEFO — one "3D Scanner" category, blank ("— None —") by default.
  const DEFO_EQUIPMENT_CATEGORIES = [
    { key: 'scanner', label: '3D Scanner', options: ['FARO Focus3D 130 HDR'], defaultValue: '' },
  ];
  // Shared helper that builds initial category picks for either MT
  // or PT. Order of preference per category:
  //   1. saved.<picksKey>[cat.key] (if its value is one of cat.options)
  //   2. any name in saved.equipment that appears in cat.options
  //   3. cat.defaultValue ('' for free-choice / Lux Meter on MT, also '' for PT)
  function _initialPicks(categories, picksKey) {
    const fromMap = (saved[picksKey] && typeof saved[picksKey] === 'object')
      ? saved[picksKey]
      : {};
    const picks = {};
    for (const cat of categories) {
      if (fromMap[cat.key] && cat.options.includes(fromMap[cat.key])) {
        picks[cat.key] = fromMap[cat.key];
      } else {
        const fromEquip = (saved.equipment || []).find((n) => cat.options.includes(n));
        picks[cat.key] = fromEquip || cat.defaultValue || '';
      }
    }
    return picks;
  }

  const [ptEquipmentPicks, setPtEquipmentPicks] = useState(() => _initialPicks(PT_EQUIPMENT_CATEGORIES, 'ptEquipmentPicks'));
  const [mtEquipmentPicks, setMtEquipmentPicks] = useState(() => _initialPicks(MT_EQUIPMENT_CATEGORIES, 'mtEquipmentPicks'));
  const [defoEquipmentPicks, setDefoEquipmentPicks] = useState(() => _initialPicks(DEFO_EQUIPMENT_CATEGORIES, 'defoEquipmentPicks'));

  const [equipment,      setEquipment]      = useState(() => {
    if (isCW) return ['Digital weld gauge'];
    if (isMT) {
      const picks = _initialPicks(MT_EQUIPMENT_CATEGORIES, 'mtEquipmentPicks');
      return MT_EQUIPMENT_CATEGORIES.map((c) => picks[c.key]).filter(Boolean);
    }
    if (isPT) {
      const picks = _initialPicks(PT_EQUIPMENT_CATEGORIES, 'ptEquipmentPicks');
      return PT_EQUIPMENT_CATEGORIES.map((c) => picks[c.key]).filter(Boolean);
    }
    if (isDEFO) {
      const picks = _initialPicks(DEFO_EQUIPMENT_CATEGORIES, 'defoEquipmentPicks');
      return DEFO_EQUIPMENT_CATEGORIES.map((c) => picks[c.key]).filter(Boolean);
    }
    if (Array.isArray(saved.equipment)) return saved.equipment;
    return [];
  });

  // Keep `equipment` in sync with whichever picker is active.
  useEffect(() => {
    if (isPT) {
      setEquipment(PT_EQUIPMENT_CATEGORIES.map((c) => ptEquipmentPicks[c.key]).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptEquipmentPicks, isPT]);
  useEffect(() => {
    if (isMT) {
      setEquipment(MT_EQUIPMENT_CATEGORIES.map((c) => mtEquipmentPicks[c.key]).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mtEquipmentPicks, isMT]);
  useEffect(() => {
    if (isDEFO) {
      setEquipment(DEFO_EQUIPMENT_CATEGORIES.map((c) => defoEquipmentPicks[c.key]).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defoEquipmentPicks, isDEFO]);
  const [showEquipModal, setShowEquipModal] = useState(false);
  // UT-only: one module per equipment, keyed by equipment name.
  // { [equipmentName]: moduleName }
  const [equipmentModules, setEquipmentModules] = useState(saved.equipmentModules ?? {});
  // Index of the equipment whose Add-Module modal is open, or null.
  const [moduleForEquipIdx, setModuleForEquipIdx] = useState(null);

  // Page 4 (UT only) — Probes & Stepwedges
  const [probes,             setProbes]             = useState(saved.probes     ?? []);
  const [stepwedges,         setStepwedges]         = useState(saved.stepwedges ?? []);
  const [showProbeModal,     setShowProbeModal]     = useState(false);
  const [showStepwedgeModal, setShowStepwedgeModal] = useState(false);

  // Page 4 — NDT Operators
  const [ndtOperators, setNdtOperators] = useState(saved.ndtOperators ?? []);
  const [ndtReporter,  setNdtReporter]  = useState(saved.ndtReporter  ?? '');
  const [showOpModal,  setShowOpModal]  = useState(false);

  // ── DDL options ───────────────────────────────────────────────
  const [terraProcedureOpts,     setTerraProcedureOpts]     = useState([]);
  const [acceptanceStandardOpts, setAcceptanceStandardOpts] = useState([]);
  const [typeOfInspectionOpts,   setTypeOfInspectionOpts]   = useState([]);
  const [surfaceConditionOpts,   setSurfaceConditionOpts]   = useState([]);
  const [insideOutsideOpts,      setInsideOutsideOpts]      = useState([]);
  const [manualOrDroneOpts,      setManualOrDroneOpts]      = useState([]);
  const [ndtReporterOpts,        setNdtReporterOpts]        = useState([]);
  const [ndtReporterItems,       setNdtReporterItems]       = useState([]);
  // MT / PT examination-page dropdown option lists.
  const [typeOfMagnetizationOpts, setTypeOfMagnetizationOpts] = useState([]);
  const [applicationMethodOpts,   setApplicationMethodOpts]   = useState([]);
  const [processingTimeOpts,      setProcessingTimeOpts]      = useState([]);
  const [typeOfWeldingOpts,       setTypeOfWeldingOpts]       = useState([]);
  const [methodOfWeldingOpts,     setMethodOfWeldingOpts]     = useState([]);

  const p = typePrefix;
  useEffect(() => subscribeToDDLByDropdownId(`${p}_terraProcedure`,     setTerraProcedureOpts),     [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_acceptanceStandard`, setAcceptanceStandardOpts), [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_typeOfInspection`,   setTypeOfInspectionOpts),   [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_surfaceCondition`,   setSurfaceConditionOpts),   [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_insideOutside`,      setInsideOutsideOpts),      [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_manualOrDrone`,      setManualOrDroneOpts),      [p]);
  useEffect(() => subscribeToDDLByDropdownId(`${p}_ndtReporter`,        setNdtReporterOpts),        [p]);
  useEffect(() => subscribeToDDLFullItemsByDropdownId(`${p}_ndtReporter`, setNdtReporterItems),     [p]);
  // MT-only dropdown (Type of Magnetization).
  useEffect(() => { if (isMT) return subscribeToDDLByDropdownId(`${p}_typeOfMagnetization`, setTypeOfMagnetizationOpts); }, [p, isMT]);
  // PT-only dropdowns (Application Method, Processing Time).
  useEffect(() => { if (isPT) return subscribeToDDLByDropdownId(`${p}_applicationMethod`, setApplicationMethodOpts); }, [p, isPT]);
  useEffect(() => { if (isPT) return subscribeToDDLByDropdownId(`${p}_processingTime`,    setProcessingTimeOpts);    }, [p, isPT]);
  // Shared MT + PT dropdowns (Type of Welding, Method of Welding).
  useEffect(() => { if (isMT || isPT) return subscribeToDDLByDropdownId(`${p}_typeOfWelding`,   setTypeOfWeldingOpts);   }, [p, isMT, isPT]);
  useEffect(() => { if (isMT || isPT) return subscribeToDDLByDropdownId(`${p}_methodOfWelding`, setMethodOfWeldingOpts); }, [p, isMT, isPT]);

  // ── UT Equipment metadata lookup (UT inspections only) ────────
  // Fetched once for the page so the equipment row can tell whether
  // each added item accepts a module. Built into a name → acceptsModule
  // map for cheap lookups during render. Returns 'yes' / 'no' / 'na' /
  // undefined.
  const [utEquipMetaByName, setUtEquipMetaByName] = useState({});
  useEffect(() => {
    if (!isUT) return;
    let cancelled = false;
    (async () => {
      try {
        const [listResp, metaResp] = await Promise.all([
          get('/equipment/by-category/UT%20Equipment'),
          get('/equipment/by-category/UT%20Equipment/metadata'),
        ]);
        if (cancelled) return;
        const items = Array.isArray(listResp?.items) ? listResp.items : [];
        const metaMap = metaResp?.metadata || {};
        const byName = {};
        for (const it of items) {
          const m = metaMap[it.id];
          if (m && it.name) {
            // Migrate legacy boolean → tristate string on the fly.
            let am = m.acceptsModule;
            if (am === true)  am = 'yes';
            if (am === false) am = 'no';
            byName[it.name] = am;
          }
        }
        setUtEquipMetaByName(byName);
      } catch {
        // Non-fatal: the wizard still works, just without per-equipment
        // module gating. Add Module button won't appear.
        if (!cancelled) setUtEquipMetaByName({});
      }
    })();
    return () => { cancelled = true; };
  }, [isUT]);

  // Equipment is "module-eligible" when its saved acceptsModule is 'yes'.
  function equipmentAcceptsModule(name) {
    return (utEquipMetaByName[name] || '').toLowerCase() === 'yes';
  }

  // ── Probe Modules cache (UT only) ──────────────────────────────
  // Used for the auto-probe suggestion that fires whenever a UTC module
  // gets attached on page 4. We only auto-add the derived name if it
  // matches a real Probe Modules entry, so we hold the names list as a
  // case-insensitive lookup map.
  const [probeModuleNames, setProbeModuleNames] = useState([]);
  useEffect(() => {
    if (!isUT) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await get('/equipment/by-category/Probe%20Modules');
        if (cancelled) return;
        const items = Array.isArray(resp?.items) ? resp.items : [];
        setProbeModuleNames(items.map((it) => it.name).filter(Boolean));
      } catch {
        if (!cancelled) setProbeModuleNames([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isUT]);

  const probeModuleByLower = useMemo(() => {
    const m = new Map();
    for (const n of probeModuleNames) m.set(n.trim().toLowerCase(), n);
    return m;
  }, [probeModuleNames]);

  function resolveProbeName(derived) {
    if (!derived) return null;
    return probeModuleByLower.get(derived.trim().toLowerCase()) || null;
  }

  // ── Auto-probe state ─────────────────────────────────────────────
  // autoProbesRef holds canonical probe names this wizard auto-added
  // (so the sync effect knows which ones it owns and can remove them
  // when the source module goes away). Held as a ref so the effect can
  // both read and update it without retriggering itself.
  const autoProbesRef = useRef(new Set());
  // Per-equipment dismissal map: when the user clicks the ✕ on an
  // auto-added probe, we record the equipments that were generating it
  // so we don't immediately re-add it while those modules are still
  // attached. Clears on module-change/clear/equipment-remove.
  const [dismissedAutoByEq, setDismissedAutoByEq] = useState({});

  // Sync the probes list against the current equipmentModules state.
  // Triggered whenever a module is added/changed/removed, the Probe
  // Modules cache loads, or the user dismisses an auto-probe.
  useEffect(() => {
    if (!isUT) return;
    // Build the set of canonical probe names that SHOULD be in `probes`
    // right now, derived from currently-attached UTC modules (skipping
    // any (eq, probe) pairs the user has dismissed).
    const desired = new Set();
    for (const [eqName, moduleName] of Object.entries(equipmentModules)) {
      if (!moduleName) continue;
      const canonical = resolveProbeName(deriveProbeNameFromModule(moduleName));
      if (!canonical) continue;
      const dismissed = dismissedAutoByEq[eqName];
      if (dismissed && dismissed.toLowerCase() === canonical.toLowerCase()) continue;
      desired.add(canonical);
    }

    setProbes((prev) => {
      let next = prev;
      let changed = false;
      const auto = autoProbesRef.current;
      const newAuto = new Set();

      // 1. Drop auto-probes whose source module is gone (or replaced).
      for (const name of auto) {
        if (desired.has(name)) {
          newAuto.add(name);
          continue;
        }
        const idx = next.findIndex((n) => String(n).trim().toLowerCase() === name.toLowerCase());
        if (idx >= 0) {
          if (!changed) { next = [...next]; changed = true; }
          next.splice(idx, 1);
        }
      }

      // 2. Add newly-desired probes — skipping duplicates and respecting
      //    MAX_PROBES so we never push past the cap.
      for (const name of desired) {
        if (newAuto.has(name)) continue;
        const exists = next.some((n) => String(n).trim().toLowerCase() === name.toLowerCase());
        if (exists) continue;
        if (next.length >= MAX_PROBES) break;
        if (!changed) { next = [...next]; changed = true; }
        next.push(name);
        newAuto.add(name);
      }

      autoProbesRef.current = newAuto;
      return changed ? next : prev;
    });
  }, [equipmentModules, probeModuleByLower, dismissedAutoByEq, isUT]);

  // List of equipment names that should have a module but don't yet.
  // Used by the incomplete-info warning.
  function equipmentMissingModule() {
    if (!isUT) return [];
    return equipment.filter((n) => equipmentAcceptsModule(n) && !equipmentModules[n]);
  }

  // ── Save / navigation ─────────────────────────────────────────
  function buildFormData() {
    // Preserve fields the wizard doesn't manage (e.g. synopsis, capturesDrivePath,
    // capturesObjectType, capturesComposition) so saving the wizard doesn't wipe them.
    return {
      ...(inspection.formData || {}),
      threeDInspectUrl,
      specificCode, terraProcedure, acceptanceStandard,
      subject,
      technique, typeOfInspection, extentOfTesting,
      surfaceCondition, surfaceTemp, insideOutside, manualOrDrone,
      couplant,
      // MT / PT examination fields. Always persisted so a type change
      // doesn't wipe data; templates ignore the keys they don't use.
      typeOfMagnetization, materialThickness,                   // MT-only
      applicationMethod,   processingTime,                       // PT-only
      typeOfWelding,       methodOfWelding,                      // shared
      // PT / MT picker maps — paired with `equipment`. Stored so a
      // reload shows the same options selected per category.
      ptEquipmentPicks,
      mtEquipmentPicks,
      defoEquipmentPicks,
      equipment,
      equipmentModules,
      probes, stepwedges,
      ndtOperators, ndtReporter,
    };
  }

  function handleNext() {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function handleBack() {
    setStep((s) => Math.max(s - 1, 0));
  }

  async function handleSaveAndClose() {
    setSaving(true);
    try {
      await updateInspectionData(inspectionId, { status: inspectionStatus, formData: buildFormData() });
      onClose();
    } catch (err) {
      console.error('Failed to save:', err);
      setSaving(false);
    }
  }

  // ── Delete ────────────────────────────────────────────────────
  async function performDelete() {
    await deleteInspection(inspectionId);
    onClose();
    navigate(`/client/${clientId}/object/${objectId}`);
  }

  function handleDeleteClick() {
    if (filled && deleteStage === 0) {
      setDeleteStage(1);
      return;
    }
    const confirmed = window.confirm('Permanently delete this inspection? This cannot be undone.');
    if (!confirmed) {
      setDeleteStage(0);
      return;
    }
    performDelete();
  }

  // ── Equipment helpers ─────────────────────────────────────────
  function addEquipmentBulk(names) {
    setEquipment((prev) => {
      const remaining = MAX_EQUIPMENT - prev.length;
      if (remaining <= 0) return prev;
      const additions = (names || []).slice(0, remaining);
      return [...prev, ...additions];
    });
  }
  function removeEquipment(idx) {
    setEquipment((p) => {
      const removed = p[idx];
      // Drop any module entry that was attached to the removed equipment,
      // and clear any auto-probe dismissal it owned so the slate is
      // clean if the same equipment is re-added later.
      if (removed) {
        setEquipmentModules((m) => {
          if (!(removed in m)) return m;
          const next = { ...m };
          delete next[removed];
          return next;
        });
        setDismissedAutoByEq((d) => {
          if (!(removed in d)) return d;
          const next = { ...d };
          delete next[removed];
          return next;
        });
      }
      return p.filter((_, i) => i !== idx);
    });
  }

  function assignModuleToEquipment(eqName, moduleName) {
    if (!eqName || !moduleName) return;
    setEquipmentModules((m) => ({ ...m, [eqName]: moduleName }));
    // Fresh module → forget any previous auto-probe dismissal for this
    // equipment so the new module's auto-probe gets a chance to appear.
    setDismissedAutoByEq((d) => {
      if (!(eqName in d)) return d;
      const next = { ...d };
      delete next[eqName];
      return next;
    });
  }
  function clearModuleForEquipment(eqName) {
    setEquipmentModules((m) => {
      if (!(eqName in m)) return m;
      const next = { ...m };
      delete next[eqName];
      return next;
    });
    setDismissedAutoByEq((d) => {
      if (!(eqName in d)) return d;
      const next = { ...d };
      delete next[eqName];
      return next;
    });
  }

  // ── Probe / Stepwedge helpers ─────────────────────────────────
  function addProbesBulk(names) {
    setProbes((prev) => {
      const remaining = MAX_PROBES - prev.length;
      if (remaining <= 0) return prev;
      return [...prev, ...(names || []).slice(0, remaining)];
    });
  }
  function removeProbe(idx) {
    setProbes((p) => {
      const name = p[idx];
      // If the user is clearing an auto-suggested probe, mark every
      // equipment currently generating that name as "dismissed" so the
      // sync effect doesn't immediately re-add it. The dismissal clears
      // when the source UTC module is removed or replaced.
      if (name && autoProbesRef.current.has(name)) {
        const lower = String(name).toLowerCase();
        const linked = [];
        for (const [eqName, moduleName] of Object.entries(equipmentModules)) {
          const canonical = resolveProbeName(deriveProbeNameFromModule(moduleName));
          if (canonical && canonical.toLowerCase() === lower) linked.push(eqName);
        }
        if (linked.length > 0) {
          setDismissedAutoByEq((d) => {
            const next = { ...d };
            for (const eq of linked) next[eq] = name;
            return next;
          });
        }
        const nextAuto = new Set(autoProbesRef.current);
        nextAuto.delete(name);
        autoProbesRef.current = nextAuto;
      }
      return p.filter((_, i) => i !== idx);
    });
  }

  function addStepwedgesBulk(names) {
    setStepwedges((prev) => {
      const remaining = MAX_STEPWEDGES - prev.length;
      if (remaining <= 0) return prev;
      return [...prev, ...(names || []).slice(0, remaining)];
    });
  }
  function removeStepwedge(idx) { setStepwedges((p) => p.filter((_, i) => i !== idx)); }

  // ── NDT operator helpers ──────────────────────────────────────
  function addNdtOperatorBulk(ops) {
    setNdtOperators((prev) => {
      const remaining = MAX_NDT_OPERATORS - prev.length;
      if (remaining <= 0) return prev;
      const additions = (ops || []).slice(0, remaining);
      return [...prev, ...additions];
    });
  }
  function removeNdtOperator(idx) { setNdtOperators((p) => p.filter((_, i) => i !== idx)); }

  const ndtReporterCert = getCertification(ndtReporterItems, ndtReporter);

  // ── Step bar ──────────────────────────────────────────────────
  function renderStepBar() {
    return (
      <div className="eom-step-bar">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <button
              className={`eom-step-btn${i === step ? ' active' : i < step ? ' done' : ''}`}
              onClick={() => setStep(i)}
              title={label}
            >
              <span className="eom-step-num">{i + 1}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`eom-step-connector${i < step ? ' done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // ── Page 0 — Info ─────────────────────────────────────────────
  function renderPage0() {
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">Info</div>

        <div className="form-group">
          <label>
            Inspection Status
            {isBeforeStart && (
              <span className="insp-status-locked-hint"> — locked until start date</span>
            )}
          </label>
          <select
            className="assign-select-full"
            value={inspectionStatus}
            onChange={(e) => setInspectionStatus(e.target.value)}
            disabled={isBeforeStart}
            title={isBeforeStart ? 'Status is locked to Planning before the start date' : undefined}
          >
            <option value="">— Select —</option>
            {STATUS_ORDER.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {!readOnly && effectiveStatus && (
            <div className="insp-effective-hint" style={{ marginTop: 8, fontSize: '13.44px' }}>
              Effective status:{' '}
              <span className="insp-status-badge" data-status={effectiveStatus}>{effectiveStatus}</span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>3D Inspect URL</label>
          <input
            ref={inspUrlInputRef}
            type="text"
            className="assign-select-full"
            value={threeDInspectUrl}
            onChange={(e) => {
              // Clear the hint on change (not on focus) so it
              // survives the refocus we trigger after a failed blur.
              if (inspUrlHint) setInspUrlHint('');
              setThreeDInspectUrl(e.target.value);
            }}
            onBlur={() => {
              // Empty → OK. Otherwise must be either
              //   /surveys/<uuid>            (Format B, canonical)
              // or
              //   /surveys/<uuid>?objectId=<uuid>   (Format A)
              // Format A gets the ?objectId=... tail stripped so we
              // always save the bare survey URL.
              const r = validateInspectionUrl(threeDInspectUrl);
              if (!r.ok) {
                setInspUrlHint(r.hint);
                setTimeout(() => inspUrlInputRef.current?.focus(), 0);
                return;
              }
              setInspUrlHint('');
              if (r.cleaned !== threeDInspectUrl) setThreeDInspectUrl(r.cleaned);
            }}
            placeholder="https://www.terra-3dinspect.com/surveys/<survey-uuid>"
          />
          {inspUrlHint && (
            <div className="form-field-hint-error">{inspUrlHint}</div>
          )}
        </div>

        {/* Danger zone — hidden in read-only ("Show more") */}
        {!readOnly && (
        <div className="eom-danger-zone">
          <p className="eom-danger-desc">
            Permanently delete this inspection. This cannot be undone.
          </p>
          <button
            className={`btn btn-outline-danger btn-sm${deleteStage === 1 ? ' insp-wizard-delete-confirming' : ''}`}
            onClick={handleDeleteClick}
          >
            {deleteStage === 1 ? 'Are you sure?' : 'Delete Inspection'}
          </button>
        </div>
        )}
      </div>
    );
  }

  // ── Page 1 — Procedure ────────────────────────────────────────
  function renderPage1() {
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">Procedure</div>

        <div className="form-group">
          <label>Specific Code</label>
          <LockedField value={specificCode} defaultValue={DEF_SPECIFIC_CODE} onChange={setSpecificCode} />
        </div>

        <div className="form-group">
          <label>Terra Procedure</label>
          {terraProcedureOpts.length > 0 ? (
            <select className="assign-select-full" value={terraProcedure} onChange={(e) => setTerraProcedure(e.target.value)}>
              <option value="">— Select —</option>
              {terraProcedureOpts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <p className="ddl-no-options-hint">No options configured for Terra Procedure.</p>
          )}
        </div>

        <div className="form-group">
          <label>Acceptance Standard</label>
          {acceptanceStandardOpts.length > 0 ? (
            <select className="assign-select-full" value={acceptanceStandard} onChange={(e) => setAcceptanceStandard(e.target.value)}>
              <option value="">— Select —</option>
              {acceptanceStandardOpts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <LockedField value={acceptanceStandard} defaultValue={DEF_ACCEPTANCE_STANDARD} onChange={setAcceptanceStandard} />
          )}
        </div>
      </div>
    );
  }

  // ── Page 2 — Examination ──────────────────────────────────────
  function renderPage2() {
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">Examination</div>

        <div className="form-group">
          <label>Subject</label>
          <LockedField value={subject} defaultValue={DEF_SUBJECT} onChange={setSubject} placeholder={DEF_SUBJECT} />
        </div>

        <div className="form-group">
          <label>Technique</label>
          <LockedField value={technique} defaultValue={DEF_TECHNIQUE} onChange={setTechnique} />
        </div>

        <div className="form-group">
          <label>Type of Inspection</label>
          {typeOfInspectionOpts.length > 0 ? (
            <select className="assign-select-full" value={typeOfInspection} onChange={(e) => setTypeOfInspection(e.target.value)}>
              <option value="">— Select —</option>
              {typeOfInspectionOpts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <p className="ddl-no-options-hint">No options configured for Type of Inspection.</p>
          )}
        </div>

        {/* MT-only fields. Slot 1 = Type of Magnetization, slot 2 =
            Material Thickness (locked-edit, "10-16" default). */}
        {isMT && (
          <>
            <div className="form-group">
              <label>Type of Magnetization</label>
              {typeOfMagnetizationOpts.length > 0 ? (
                <select className="assign-select-full" value={typeOfMagnetization} onChange={(e) => setTypeOfMagnetization(e.target.value)}>
                  <option value="">— Select —</option>
                  {typeOfMagnetizationOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <p className="ddl-no-options-hint">No options configured for Type of Magnetization.</p>
              )}
            </div>

            <div className="form-group">
              <label>Material Thickness</label>
              <LockedField
                value={materialThickness}
                defaultValue={DEF_MATERIAL_THICKNESS}
                onChange={setMaterialThickness}
                allowedRegex={/^[0-9/ \-]$/}
              />
            </div>
          </>
        )}

        {/* PT-only fields. Slot 1 = Application Method, slot 2 =
            Processing Time. Replaces MT's Type-of-Magnetization +
            Material-Thickness pair while keeping the same layout
            position on the page. */}
        {isPT && (
          <>
            <div className="form-group">
              <label>Penetrant Application Method</label>
              {applicationMethodOpts.length > 0 ? (
                <select className="assign-select-full" value={applicationMethod} onChange={(e) => setApplicationMethod(e.target.value)}>
                  <option value="">— Select —</option>
                  {applicationMethodOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <p className="ddl-no-options-hint">No options configured for Application Method.</p>
              )}
            </div>

            <div className="form-group">
              <label>Processing Time (mins)</label>
              {processingTimeOpts.length > 0 ? (
                <select className="assign-select-full" value={processingTime} onChange={(e) => setProcessingTime(e.target.value)}>
                  <option value="">— Select —</option>
                  {processingTimeOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <p className="ddl-no-options-hint">No options configured for Processing Time.</p>
              )}
            </div>
          </>
        )}

        {/* Shared MT + PT fields. */}
        {(isMT || isPT) && (
          <>
            <div className="form-group">
              <label>Type of Welding</label>
              {typeOfWeldingOpts.length > 0 ? (
                <select className="assign-select-full" value={typeOfWelding} onChange={(e) => setTypeOfWelding(e.target.value)}>
                  <option value="">— Select —</option>
                  {typeOfWeldingOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <p className="ddl-no-options-hint">No options configured for Type of Welding.</p>
              )}
            </div>

            <div className="form-group">
              <label>Method of Welding</label>
              {methodOfWeldingOpts.length > 0 ? (
                <select className="assign-select-full" value={methodOfWelding} onChange={(e) => setMethodOfWelding(e.target.value)}>
                  <option value="">— Select —</option>
                  {methodOfWeldingOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <p className="ddl-no-options-hint">No options configured for Method of Welding.</p>
              )}
            </div>
          </>
        )}

        <div className="form-group">
          <label>Extent of Testing</label>
          <LockedField value={extentOfTesting} defaultValue={DEF_EXTENT_OF_TESTING} onChange={setExtentOfTesting} />
        </div>

        <div className="form-group">
          <label>Surface Condition</label>
          {surfaceConditionOpts.length > 0 ? (
            <select className="assign-select-full" value={surfaceCondition} onChange={(e) => setSurfaceCondition(e.target.value)}>
              <option value="">— Select —</option>
              {surfaceConditionOpts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <p className="ddl-no-options-hint">No options configured for Surface Condition.</p>
          )}
        </div>

        <div className="form-group">
          <label>Surface Temperature (°C)</label>
          <LockedField value={surfaceTemp} defaultValue={DEF_SURFACE_TEMP} onChange={setSurfaceTemp} numericOnly={true} />
        </div>

        {isUT && (
          <div className="form-group">
            <label>Couplant</label>
            <LockedField value={couplant} defaultValue={DEF_COUPLANT} onChange={setCouplant} placeholder={DEF_COUPLANT} />
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Inside / Outside</label>
            {insideOutsideOpts.length > 0 ? (
              <select className="assign-select-full" value={insideOutside} onChange={(e) => setInsideOutside(e.target.value)}>
                <option value="">— Select —</option>
                {insideOutsideOpts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <p className="ddl-no-options-hint">No options configured.</p>
            )}
          </div>
          <div className="form-group">
            <label>Manual / Drone</label>
            {manualOrDroneOpts.length > 0 ? (
              <select className="assign-select-full" value={manualOrDrone} onChange={(e) => setManualOrDrone(e.target.value)}>
                <option value="">— Select —</option>
                {manualOrDroneOpts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <p className="ddl-no-options-hint">No options configured.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Page 3 — Equipment ────────────────────────────────────────
  function renderPage3() {
    const atLimit = equipment.length >= MAX_EQUIPMENT;
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">Equipment</div>
        <p className="insp-wizard-page-hint">
          {isCW
            ? 'CW-OSI uses one fixed instrument: Digital weld gauge.'
            : isMT
              ? 'Magnetic Inspection — pick one option per category.'
              : isPT
                ? 'Penetrant Inspection — pick one option per category.'
                : `Add the equipment used during this inspection. Maximum ${MAX_EQUIPMENT} items.`}
        </p>

        {/* MT / PT category picker. One select per category with a
            "— None —" entry so the user can deliberately clear a
            category. Inline warnings are NOT rendered here; missing
            picks surface in the inspection-detail incomplete-info
            banner under the card. Final `equipment` array is rebuilt
            by the corresponding sync effect. */}
        {(isMT || isPT || isDEFO) && (
          <div
            className="insp-wizard-items-list"
            style={{ border: 'none', padding: 0 }}
          >
            {(isMT ? MT_EQUIPMENT_CATEGORIES : isPT ? PT_EQUIPMENT_CATEGORIES : DEFO_EQUIPMENT_CATEGORIES).map((cat) => {
              const picks = isMT ? mtEquipmentPicks : isPT ? ptEquipmentPicks : defoEquipmentPicks;
              const setPicks = isMT ? setMtEquipmentPicks : isPT ? setPtEquipmentPicks : setDefoEquipmentPicks;
              const value = picks[cat.key] || '';
              return (
                <div key={cat.key} className="form-group">
                  <label>{cat.label}</label>
                  <select
                    className="assign-select-full"
                    value={value}
                    onChange={(e) => setPicks((prev) => ({ ...prev, [cat.key]: e.target.value }))}
                  >
                    <option value="">— None —</option>
                    {cat.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}

        {!isPT && !isMT && !isDEFO && equipment.length > 0 && (
          <div className="insp-wizard-items-list">
            {equipment.map((item, idx) => {
              const moduleName    = equipmentModules[item];
              const acceptsModule = isUT && equipmentAcceptsModule(item);
              return (
                <div key={idx} className="insp-wizard-item-row">
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                    <span className="insp-wizard-item-name">{item}</span>
                    {acceptsModule && moduleName && (
                      <span className="insp-wizard-item-cert">UTC Module: <strong>{moduleName}</strong></span>
                    )}
                    {acceptsModule && !moduleName && (
                      <span className="insp-wizard-item-cert" style={{ color: 'var(--warning)' }}>
                        Missing UTC module
                      </span>
                    )}
                  </div>
                  {/* Add Module button appears only when the equipment's
                      Accepts-module metadata says 'yes'. No/Not applicable
                      or unset hides the button entirely. */}
                  {acceptsModule && (
                    moduleName ? (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => clearModuleForEquipment(item)}
                        title="Remove the attached UTC module"
                        style={{ margin: 0 }}
                      >
                        Remove UTC module
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setModuleForEquipIdx(idx)}
                        title="Attach a UTC module to this equipment"
                        style={{ marginRight: '8px' }}
                      >
                        + Add UTC Module
                      </button>
                    )
                  )}
                  {/* CW-OSI / MT / PT have fixed equipment lists — no remove. */}
                  {!isCW && !isMT && !isPT && (
                    <button className="insp-wizard-item-remove" onClick={() => removeEquipment(idx)} title="Remove">✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add Equipment toolbar hidden for CW-OSI / MT / PT / DEFO (fixed lists). */}
        {!isCW && !isMT && !isPT && !isDEFO && (
          <div className="insp-wizard-add-row">
            <button className="btn btn-secondary" onClick={() => setShowEquipModal(true)} disabled={atLimit}>
              + Add Equipment
            </button>
            {atLimit && <span className="insp-wizard-limit-hint">Maximum {MAX_EQUIPMENT} items reached.</span>}
          </div>
        )}

        {showEquipModal && (
          equipmentCategory ? (
            <AddEquipmentModal
              category={equipmentCategory}
              alreadyAdded={equipment}
              maxItems={MAX_EQUIPMENT}
              onClose={() => setShowEquipModal(false)}
              onAdd={addEquipmentBulk}
            />
          ) : (
            <FreeTextEquipmentModal
              onClose={() => setShowEquipModal(false)}
              onAdd={addEquipmentBulk}
            />
          )
        )}

        {/* UT-only: one module per equipment. The Modules cache already
            filters to entries with equipmentType.name == "Module" so the
            picker only shows valid modules. */}
        {isUT && moduleForEquipIdx !== null && equipment[moduleForEquipIdx] && (
          <AddEquipmentModal
            category="UTC Modules"
            alreadyAdded={[]}
            maxItems={1}
            title="Add UTC Module"
            onClose={() => setModuleForEquipIdx(null)}
            onAdd={(picked) => {
              const eqName = equipment[moduleForEquipIdx];
              const mod = (picked || [])[0];
              if (eqName && mod) assignModuleToEquipment(eqName, mod);
              setModuleForEquipIdx(null);
            }}
          />
        )}
      </div>
    );
  }

  // ── Page (UT only) — Probes & Stepwedges ─────────────────────
  function renderPageProbes() {
    const probesAtLimit     = probes.length     >= MAX_PROBES;
    const stepwedgesAtLimit = stepwedges.length >= MAX_STEPWEDGES;
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">Probe Modules</div>

        <p className="insp-wizard-page-hint">
          Add the probe modules used during this inspection. Maximum {MAX_PROBES} probe modules.
        </p>

        {probes.length > 0 && (
          <div className="insp-wizard-items-list">
            {probes.map((item, idx) => (
              <div key={idx} className="insp-wizard-item-row">
                <span className="insp-wizard-item-name">{item}</span>
                <button className="insp-wizard-item-remove" onClick={() => removeProbe(idx)} title="Remove">✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="insp-wizard-add-row">
          <button className="btn btn-secondary" onClick={() => setShowProbeModal(true)} disabled={probesAtLimit}>
            + Add Probe Module
          </button>
          {probesAtLimit && <span className="insp-wizard-limit-hint">Maximum {MAX_PROBES} probe modules reached.</span>}
        </div>

        <div style={{ height: '1.5rem' }} />

        <div className="insp-wizard-page-title">Stepwedges</div>
        <p className="insp-wizard-page-hint">
          Add the stepwedges used during this inspection. Maximum {MAX_STEPWEDGES} stepwedges.
        </p>

        {stepwedges.length > 0 && (
          <div className="insp-wizard-items-list">
            {stepwedges.map((item, idx) => (
              <div key={idx} className="insp-wizard-item-row">
                <span className="insp-wizard-item-name">{item}</span>
                <button className="insp-wizard-item-remove" onClick={() => removeStepwedge(idx)} title="Remove">✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="insp-wizard-add-row">
          <button className="btn btn-secondary" onClick={() => setShowStepwedgeModal(true)} disabled={stepwedgesAtLimit}>
            + Add Stepwedge
          </button>
          {stepwedgesAtLimit && <span className="insp-wizard-limit-hint">Maximum {MAX_STEPWEDGES} stepwedges reached.</span>}
        </div>

        {showProbeModal && (
          <AddEquipmentModal
            category="Probe Modules"
            alreadyAdded={probes}
            maxItems={MAX_PROBES}
            onClose={() => setShowProbeModal(false)}
            onAdd={addProbesBulk}
            title="Add Probe Module"
          />
        )}
        {showStepwedgeModal && (
          <AddEquipmentModal
            category="Step wedges"
            alreadyAdded={stepwedges}
            maxItems={MAX_STEPWEDGES}
            onClose={() => setShowStepwedgeModal(false)}
            onAdd={addStepwedgesBulk}
            title="Add Stepwedge"
          />
        )}
      </div>
    );
  }

  // ── Page 4 — NDT Operators ────────────────────────────────────
  function renderPage4() {
    const atLimit = ndtOperators.length >= MAX_NDT_OPERATORS;
    return (
      <div className="insp-wizard-page-content">
        <div className="insp-wizard-page-title">NDT Operators</div>
        <p className="insp-wizard-page-hint">
          Add the NDT operators who performed this inspection. Maximum {MAX_NDT_OPERATORS} operators.
        </p>

        {ndtOperators.length > 0 && (
          <div className="insp-wizard-items-list">
            {ndtOperators.map((op, idx) => (
              <div key={idx} className="insp-wizard-item-row">
                <div style={{ flex: 1 }}>
                  <span className="insp-wizard-item-name">{op.name}</span>
                  {op.certification && <span className="insp-wizard-item-cert"> — {op.certification}</span>}
                </div>
                <button className="insp-wizard-item-remove" onClick={() => removeNdtOperator(idx)} title="Remove">✕</button>
              </div>
            ))}
          </div>
        )}

        <div className="insp-wizard-add-row">
          <button className="btn btn-secondary" onClick={() => setShowOpModal(true)} disabled={atLimit}>
            + Add NDT Operator
          </button>
          {atLimit && <span className="insp-wizard-limit-hint">Maximum {MAX_NDT_OPERATORS} operators reached.</span>}
        </div>

        {/* NDT Reporter — below operators, no divider */}
        <div className="insp-wizard-reporter-section">
          <div className="form-group">
            <label>NDT Reporter</label>
            {ndtReporterOpts.length > 0 ? (
              <select
                className="assign-select-full"
                value={ndtReporter}
                onChange={(e) => setNdtReporter(e.target.value)}
              >
                <option value="">— Select —</option>
                {ndtReporterOpts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <p className="ddl-no-options-hint">No options configured for NDT Reporter.</p>
            )}
            {ndtReporter && (
              <div className={`insp-wizard-certification${!ndtReporterCert ? ' insp-wizard-cert-none' : ''}`}>
                {ndtReporterCert
                  ? <>Certification: <strong>{ndtReporterCert}</strong></>
                  : 'No certification on record'
                }
              </div>
            )}
          </div>
        </div>

        {showOpModal && (
          <AddNDTOperatorModal
            ndtOperatorCollection={ndtOperatorCollection}
            alreadyAdded={ndtOperators}
            maxItems={MAX_NDT_OPERATORS}
            onClose={() => setShowOpModal(false)}
            onAdd={addNdtOperatorBulk}
          />
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  const pages      = isUT
    ? [renderPage0, renderPage1, renderPage2, renderPage3, renderPageProbes, renderPage4]
    : [renderPage0, renderPage1, renderPage2, renderPage3, renderPage4];
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="modal-overlay insp-wizard-overlay">
      <div className="modal insp-wizard-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="insp-wizard-header-title">
            <span className="insp-wizard-header-label">{typeLabel || 'Inspection'}</span>
            {objectTag && <span className="insp-wizard-header-badge">{objectTag}</span>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {renderStepBar()}

        <div className="insp-wizard-scroll-content">
          <fieldset className="insp-wizard-fieldset" disabled={readOnly}>
          {pages[step]()}
          </fieldset>
        </div>

        {readOnly ? (
          <div className="insp-wizard-footer">
            <div className="insp-wizard-footer-left" />
            <div className="insp-wizard-footer-right">
              {step > 0 && (
                <button className="btn btn-secondary" onClick={handleBack}>← Back</button>
              )}
              {isLastStep ? (
                <button className="btn btn-primary" onClick={onClose}>Close</button>
              ) : (
                <button className="btn btn-primary" onClick={handleNext}>Next →</button>
              )}
            </div>
          </div>
        ) : (
          <div className="insp-wizard-footer">
            <div className="insp-wizard-footer-left">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            </div>
            <div className="insp-wizard-footer-right">
              {step > 0 && (
                <button className="btn btn-secondary" onClick={handleBack} disabled={saving}>← Back</button>
              )}
              {isLastStep ? (
                <button className="btn btn-primary" onClick={handleSaveAndClose} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleNext}>Next →</button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
